/* ============================================================
   Section: combo-main-product  (templates/product.combo-pdp.json)
   ------------------------------------------------------------
   Gallery carousel + mobile sticky bar for the combo PDP.

   Add-to-cart is NOT handled here: both the main button and the
   sticky bar are wired to the theme's shared Alpine pipeline
   (x-data="xProductCart" in the Liquid), so cart state, drawer
   re-render and notifications stay on one code path.
   ============================================================ */

const CMP_MIN_QTY = 1;
const CMP_MAX_QTY = 10;
const CMP_TICKER_INTERVAL = 3000;

class ComboMainProduct extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-cmp-track]');
    this.dotsWrap = this.querySelector('[data-cmp-dots]');
    this.stickyBar = this.querySelector('[data-cmp-sticky-bar]');
    this.quantity = CMP_MIN_QTY;

    this.handleTrackScroll = this.handleTrackScroll.bind(this);
    this.handleDotsClick = this.handleDotsClick.bind(this);
    this.handleArrowClick = this.handleArrowClick.bind(this);
    this.handleStickyQtyClick = this.handleStickyQtyClick.bind(this);

    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleDropdownClick = this.handleDropdownClick.bind(this);
    this.handleDropdownKeydown = this.handleDropdownKeydown.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);

    this.initCarousel();
    this.initVariants();
    this.initStickyBar();
    this.initTicker();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleTrackScroll);
    this.dotsWrap?.removeEventListener('click', this.handleDotsClick);
    this.removeEventListener('click', this.handleArrowClick);
    this.stickyBar?.removeEventListener('click', this.handleStickyQtyClick);
    this.picker?.removeEventListener('change', this.handleOptionChange);
    this.picker?.removeEventListener('click', this.handleDropdownClick);
    this.picker?.removeEventListener('keydown', this.handleDropdownKeydown);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    this.observer?.disconnect();

    if (this.tickerTimer) {
      clearInterval(this.tickerTimer);
      this.tickerTimer = null;
    }
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  /* ---------- Gallery carousel ---------- */

  initCarousel() {
    if (!this.track) return;

    this.allSlides = [...this.querySelectorAll('[data-cmp-slide]')];
    this.allDots = [...this.querySelectorAll('[data-cmp-dot]')];
    this.collectVisibleSlides();
    this.activeIndex = Number(this.track.dataset.startIndex ?? 0);

    if (this.allSlides.length < 2) return;

    if (this.activeIndex > 0) this.track.scrollLeft = this.activeIndex * this.track.clientWidth;

    this.track.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    this.dotsWrap?.addEventListener('click', this.handleDotsClick);
    this.addEventListener('click', this.handleArrowClick);

    this.syncArrows();
  }

  handleTrackScroll() {
    /* Coalesce scroll events into one read per frame. */
    if (this.scrollFrame) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;

      const { scrollLeft, clientWidth } = this.track;
      const index = Math.round(scrollLeft / clientWidth);
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));

      if (clamped !== this.activeIndex) {
        this.activeIndex = clamped;
        this.syncDots();
      }
      this.syncArrows();
    });
  }

  handleDotsClick(event) {
    const dot = event.target.closest('[data-cmp-dot]');
    if (!dot) return;

    const slide = this.allSlides[Number(dot.dataset.index ?? 0)];
    const index = this.slides.indexOf(slide);
    if (index >= 0) this.scrollToSlide(index);
  }

  handleArrowClick(event) {
    const arrow = event.target.closest('[data-cmp-prev], [data-cmp-next]');
    if (!arrow) return;

    const step = arrow.hasAttribute('data-cmp-next') ? 1 : -1;
    this.scrollToSlide(this.activeIndex + step);
  }

  scrollToSlide(index) {
    const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.track.scrollTo({
      left: clamped * this.track.clientWidth,
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  }

  syncDots() {
    const activeSlide = this.slides[this.activeIndex];

    for (const [index, dot] of this.allDots.entries()) {
      const isActive = this.allSlides[index] === activeSlide;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    }
  }

  collectVisibleSlides() {
    this.slides = this.allSlides.filter((slide) => !slide.hidden);

    const single = this.slides.length < 2;
    for (const control of this.querySelectorAll('[data-cmp-prev], [data-cmp-next], [data-cmp-dots]')) {
      control.toggleAttribute('hidden', single);
    }
  }

  showVariantMedia(variant) {
    if (!this.allSlides?.length) return;

    const list = Array.isArray(variant.mediaList) ? variant.mediaList : [];
    const shown = new Set(list);

    for (const [index, slide] of this.allSlides.entries()) {
      const hidden = shown.size > 0 && !shown.has(index);
      slide.hidden = hidden;
      if (this.allDots[index]) this.allDots[index].hidden = hidden;
    }

    this.collectVisibleSlides();

    const target = shown.size > 0 ? 0 : Math.max(0, variant.mediaIndex);
    this.activeIndex = target;
    this.track.scrollLeft = target * this.track.clientWidth;
    this.syncDots();
    this.syncArrows();
  }

  syncArrows() {
    const prev = this.querySelector('[data-cmp-prev]');
    const next = this.querySelector('[data-cmp-next]');
    if (prev) prev.disabled = this.activeIndex <= 0;
    if (next) next.disabled = this.activeIndex >= this.slides.length - 1;
  }

  /* ---------- Variants ----------
     Money arrives pre-formatted from Liquid, so nothing here builds a price
     string. Every node it touches is already in the DOM; switching a variant
     only swaps text and toggles `hidden`/`disabled`. */

  initVariants() {
    this.picker = this.querySelector('[data-cmp-variant-picker]');

    const dataEl = this.querySelector('[data-cmp-variant-data]');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch {
      this.variants = [];
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) return;

    /* No picker (single-variant product) — the matrix is still useful to the
       sticky bar, but there is nothing to listen to. */
    if (!this.picker) return;

    this.picker.addEventListener('change', this.handleOptionChange);

    if (this.picker.querySelector('[data-cmp-dropdown]')) {
      this.picker.addEventListener('click', this.handleDropdownClick);
      this.picker.addEventListener('keydown', this.handleDropdownKeydown);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
    }
  }

  handleOptionChange(event) {
    if (!event.target.closest('[data-cmp-option-input]')) return;
    this.resolveVariant();
  }

  resolveVariant() {
    /* One value per option, in option order: the dropdown's hidden input, or
       the checked radio in a tab group. */
    const chosen = [...this.picker.querySelectorAll('[data-cmp-option-input]')]
      .filter((input) => input.type === 'hidden' || input.checked)
      .map((input) => input.value);

    const variant = this.variants.find(
      (v) => v.options.length === chosen.length && v.options.every((opt, i) => opt === chosen[i])
    );

    if (variant) this.applyVariant(variant);
  }

  /* ---------- Variant dropdown ----------
     Same behaviour as the Variant Option Picker dropdown in
     main-product-section: the toggle opens and closes the list, a click
     outside or Escape closes it, and picking a value closes it. */

  handleDropdownClick(event) {
    const option = event.target.closest('[data-cmp-dropdown-option]');
    if (option) {
      this.selectDropdownOption(option);
      return;
    }

    const toggle = event.target.closest('[data-cmp-dropdown-toggle]');
    if (!toggle) return;

    const dropdown = toggle.closest('[data-cmp-dropdown]');
    this.setDropdownOpen(dropdown, !this.isDropdownOpen(dropdown));
  }

  handleDropdownKeydown(event) {
    const { key } = event;
    const option = event.target.closest('[data-cmp-dropdown-option]');
    const toggle = event.target.closest('[data-cmp-dropdown-toggle]');
    if (!option && !toggle) return;

    const dropdown = event.target.closest('[data-cmp-dropdown]');

    if (key === 'Escape') {
      this.setDropdownOpen(dropdown, false);
      dropdown.querySelector('[data-cmp-dropdown-toggle]')?.focus();
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (option) this.selectDropdownOption(option);
      else this.setDropdownOpen(dropdown, !this.isDropdownOpen(dropdown));
      return;
    }

    /* Arrow keys move through the values, opening the list if needed. */
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      const options = [...dropdown.querySelectorAll('[data-cmp-dropdown-option]')];
      const current = option ?? dropdown.querySelector('[data-cmp-dropdown-option].is-active');
      const step = key === 'ArrowDown' ? 1 : -1;
      const index = Math.max(0, Math.min(options.length - 1, options.indexOf(current) + (option ? step : 0)));

      this.setDropdownOpen(dropdown, true);
      options[index]?.focus();
    }
  }

  handleDocumentClick(event) {
    for (const dropdown of this.querySelectorAll('[data-cmp-dropdown]')) {
      if (!dropdown.contains(event.target)) this.setDropdownOpen(dropdown, false);
    }
  }

  handleDocumentKeydown(event) {
    if (event.key !== 'Escape') return;
    for (const dropdown of this.querySelectorAll('[data-cmp-dropdown]')) {
      this.setDropdownOpen(dropdown, false);
    }
  }

  isDropdownOpen(dropdown) {
    return dropdown?.querySelector('[data-cmp-dropdown-toggle]')?.getAttribute('aria-expanded') === 'true';
  }

  setDropdownOpen(dropdown, open) {
    const toggle = dropdown?.querySelector('[data-cmp-dropdown-toggle]');
    const menu = dropdown?.querySelector('[data-cmp-dropdown-menu]');
    if (!toggle || !menu) return;

    menu.toggleAttribute('hidden', !open);
    toggle.setAttribute('aria-expanded', String(open));
  }

  selectDropdownOption(option) {
    const dropdown = option.closest('[data-cmp-dropdown]');
    const { optionValue } = option.dataset;

    for (const item of dropdown.querySelectorAll('[data-cmp-dropdown-option]')) {
      const isActive = item === option;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', String(isActive));
    }

    const value = dropdown.querySelector('[data-cmp-dropdown-value]');
    if (value) value.textContent = optionValue;

    const input = dropdown.querySelector('[data-cmp-option-input]');
    if (input) input.value = optionValue;

    this.setDropdownOpen(dropdown, false);
    dropdown.querySelector('[data-cmp-dropdown-toggle]')?.focus();
    this.resolveVariant();
  }

  applyVariant(variant) {
    /* Cart forms — main and sticky. */
    for (const input of this.querySelectorAll('[data-cmp-variant-input], [data-cmp-sticky-variant-input]')) {
      input.value = variant.id;
    }
    this.dataset.variantId = variant.id;

    this.updateTitle(variant);
    this.updatePrice(variant);
    this.updateOfferTag(variant);
    this.updateAddToCart(variant);

    /* Keep the address bar shareable without adding a history entry. */
    if (variant.url) {
      window.history.replaceState({}, '', variant.url);
    }

    this.showVariantMedia(variant);

    document.dispatchEvent(new CustomEvent('theme:variant:changed', {
      detail: { variant, sectionId: this.dataset.sectionId },
      bubbles: true,
    }));
  }

  updatePrice(variant) {
    const priceEl = this.querySelector('[data-cmp-price]');
    if (!priceEl) return;

    const sale = priceEl.querySelector('[data-cmp-price-sale]');
    if (sale) sale.textContent = variant.price;

    const showCompare = priceEl.dataset.cmpShowCompare === 'true';
    const compare = priceEl.querySelector('[data-cmp-price-compare]');
    if (compare) {
      compare.textContent = variant.compare;
      compare.toggleAttribute('hidden', !(showCompare && variant.hasCompare));
    }

    const pct = priceEl.querySelector('[data-cmp-price-pct]');
    if (pct) {
      pct.textContent = `${variant.pct}% OFF`;
      pct.toggleAttribute('hidden', variant.pct <= 0);
    }

    /* A price note takes the tax note's place. */
    const note = priceEl.querySelector('[data-cmp-price-note]');
    if (note) {
      note.textContent = variant.priceNote;
      note.toggleAttribute('hidden', !variant.priceNote);
    }
    priceEl.querySelector('[data-cmp-price-tax]')?.toggleAttribute('hidden', Boolean(variant.priceNote));

    for (const el of this.querySelectorAll('[data-cmp-atc-price], [data-cmp-sticky-atc-price]')) {
      el.textContent = variant.price;
    }
  }

  /* Both copies — mobile head and desktop purchase card. */
  updateTitle(variant) {
    if (!variant.pdpTitle) return;

    for (const title of this.querySelectorAll('[data-cmp-title]')) {
      title.textContent = variant.pdpTitle;
    }
  }

  updateOfferTag(variant) {
    const offer = this.querySelector('[data-cmp-offer]');
    if (!offer) return;

    const text = offer.querySelector('[data-cmp-offer-text]');
    if (text) text.textContent = variant.offerTag;
    offer.toggleAttribute('hidden', !variant.offerTag);
  }

  updateAddToCart(variant) {
    const buttons = [
      this.querySelector('[data-cmp-atc-btn]'),
      this.querySelector('[data-cmp-sticky-atc-btn]'),
    ].filter(Boolean);

    for (const button of buttons) {
      button.disabled = !variant.available;
      button.classList.toggle('section-cmp__sold-out', !variant.available);

      const label = button.querySelector('[data-cmp-atc-label], [data-cmp-sticky-atc-label]');
      if (label) {
        const { labelAvailable, labelSoldout } = button.dataset;
        label.textContent = variant.available ? labelAvailable : labelSoldout;
      }

      const priceWrap = button.querySelector('[data-cmp-atc-price-wrap], [data-cmp-sticky-atc-price-wrap]');
      if (priceWrap) priceWrap.toggleAttribute('hidden', !variant.available);
    }
  }

  /* ---------- Mobile sticky bar ---------- */

  initStickyBar() {
    if (!this.stickyBar) return;

    this.stickyBar.addEventListener('click', this.handleStickyQtyClick);

    /* Reveal the bar only once the main Add to Cart has scrolled away,
       so the two CTAs are never on screen at the same time. */
    const mainAtc = this.querySelector('[data-cmp-atc-btn]');

    if (mainAtc && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const hidden = entry.isIntersecting;
            this.stickyBar.classList.toggle('is-visible', !hidden);
            this.stickyBar.setAttribute('aria-hidden', String(hidden));
          }
        },
        { rootMargin: '0px', threshold: 0 }
      );
      this.observer.observe(mainAtc);
    } else {
      this.stickyBar.classList.add('is-visible');
      this.stickyBar.setAttribute('aria-hidden', 'false');
    }

    this.syncQuantity();
  }

  handleStickyQtyClick(event) {
    const button = event.target.closest('[data-cmp-sticky-qty-dec], [data-cmp-sticky-qty-inc]');
    if (!button) return;

    const step = button.hasAttribute('data-cmp-sticky-qty-inc') ? 1 : -1;
    const next = Math.max(CMP_MIN_QTY, Math.min(CMP_MAX_QTY, this.quantity + step));
    if (next === this.quantity) return;

    this.quantity = next;
    this.syncQuantity();
  }

  /* Mirror the chosen quantity into both cart forms. Price is left exactly as
     Liquid rendered it — money formatting never happens in JS. */
  syncQuantity() {
    const value = this.querySelector('[data-cmp-sticky-qty-value]');
    if (value) value.textContent = this.quantity;

    for (const input of this.querySelectorAll('[data-cmp-quantity-input], [data-cmp-sticky-quantity-input]')) {
      input.value = this.quantity;
    }

    const dec = this.querySelector('[data-cmp-sticky-qty-dec]');
    const inc = this.querySelector('[data-cmp-sticky-qty-inc]');
    if (dec) dec.disabled = this.quantity <= CMP_MIN_QTY;
    if (inc) inc.disabled = this.quantity >= CMP_MAX_QTY;
  }

  /* ---------- Sticky bar ticker ---------- */

  initTicker() {
    const ticker = this.querySelector('[data-cmp-sticky-ticker]');
    if (!ticker) return;

    const items = [...ticker.children];
    if (items.length < 2) return;

    let current = 0;

    this.tickerTimer = setInterval(() => {
      const outgoing = items[current];
      current = (current + 1) % items.length;
      const incoming = items[current];

      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-exiting');
      incoming.classList.add('is-active');

      setTimeout(() => outgoing.classList.remove('is-exiting'), 400);
    }, CMP_TICKER_INTERVAL);
  }
}

if (!customElements.get('combo-main-product')) {
  customElements.define('combo-main-product', ComboMainProduct);
}
